/**
 * The link graph: how everything in Apiboo connects (Graph & Projects, 13.09.2026).
 *
 * `buildGraph(snapshot)` is PURE — the app's stores go in (graphSnapshot.js reads
 * them), `{ version, nodes, edges }` comes out. The same object feeds the graph
 * page and, later, the MCP graph tools, so an AI and the user see one truth.
 *
 * What a node carries: id, type, label, and a few non-secret facts (method,
 * engine, scope). NEVER content: no document body, no request body, no variable
 * value, no vault field beyond the item's name. Bodies and requests are READ to
 * find links; only the link and where it was found (`anchor`) leave this module.
 *
 * Edge origins:
 *   explicit — the user made it (a project membership, a "Link to…")
 *   derived  — worked out from data the user already has (hierarchy, {{vars}},
 *              page links inside documents)
 */

import { projectSolidColor } from './projectColors';
import { withoutUser } from './repoRefs';
import { taskStatusOf } from '../extensions/bundled/task-manager/taskStatus';
import { addDocWorkspaces } from './graphDocWorkspaces';

export const GRAPH_VERSION = 1;

export const NODE_TYPES = Object.freeze([
  'project',
  'collection',
  'folder',
  'request',
  'environment',
  'board',
  'task',
  'doc_folder',
  'document',
  'doc_workspace', // a user-defined document workspace (card 5.4): groups its root folders/pages
  'appointment',
  'db_connection',
  'ssh_host',
  'git_repo',
]);

export const RELATIONS = Object.freeze([
  'contains', // structural hierarchy, and project membership
  'uses', // request → environment defining a {{variable}} it references
  'mentions', // document → document page link
  'relates_to', // explicit, generic
  'covers', // explicit: task covers a request/collection
  'depends_on', // explicit
  'documents', // explicit: document documents an item
]);

/** Relations a user may choose for an explicit link. */
export const EXPLICIT_RELATIONS = Object.freeze(['relates_to', 'covers', 'depends_on', 'documents']);

export function nodeId(type, id) {
  return `${type}:${id}`;
}

/** `request:abc` → { type: 'request', id: 'abc' }; ids may themselves contain ':'. */
export function parseNodeId(value) {
  const s = String(value || '');
  const i = s.indexOf(':');
  if (i <= 0) return null;
  const type = s.slice(0, i);
  if (!NODE_TYPES.includes(type)) return null;
  return { type, id: s.slice(i + 1) };
}

const VAR_RE = /\{\{\s*([A-Za-z0-9_.\-$]+)\s*\}\}/g;

/** Variable NAMES referenced anywhere in a string. Dynamic `$vars` are skipped. */
export function referencedVariables(text) {
  const out = new Set();
  if (typeof text !== 'string' || !text.includes('{{')) return out;
  for (const m of text.matchAll(VAR_RE)) {
    if (!m[1].startsWith('$')) out.add(m[1]);
  }
  return out;
}

/**
 * The request fields (collectionRecords.js buildRequest shape) a {{variable}}
 * can be used in. Scripts (`preRequestScript`, `testScript`) are deliberately
 * NOT scanned: MCP may not read scripts at all (O33), and an edge saying "this
 * script reads `apiToken`" would leak script content through the graph.
 */
const REQUEST_FIELDS = ['url', 'headers', 'params', 'cookies', 'body', 'formDataFields', 'auth'];

export function requestVariableUses(request) {
  const uses = new Map(); // name → first field it appeared in
  for (const field of REQUEST_FIELDS) {
    const raw = request?.[field];
    if (raw == null || raw === '') continue;
    const text = typeof raw === 'string' ? raw : safeJson(raw);
    for (const name of referencedVariables(text)) if (!uses.has(name)) uses.set(name, field);
  }
  return uses;
}

function safeJson(v) {
  try {
    return JSON.stringify(v);
  } catch {
    return '';
  }
}

const DOC_LINK_RE = /data-doc-link\s*=\s*"([^"]+)"/g;

/** Document ids a document body links to (PageLinkNode's saved HTML). */
export function documentLinks(bodyHtml) {
  const out = new Set();
  if (typeof bodyHtml !== 'string' || !bodyHtml.includes('data-doc-link')) return out;
  for (const m of bodyHtml.matchAll(DOC_LINK_RE)) out.add(m[1]);
  return out;
}

/** The scope facts a record-backed node carries, when its row has them. */
const whereOf = (row) => {
  if (!row?.scope) return undefined;
  return row.location ? { scope: row.scope, location: row.location } : { scope: row.scope };
};

const str = (v, fallback) => {
  const s = v == null ? '' : String(v).trim();
  return s || fallback;
};

/**
 * @param {object} s snapshot — every key optional:
 *   collections   [{ id, name, requests:[{id,name,method,folder_id,url,…}], settings:{folders:[{id,name,parent_id}]} }]
 *   environments  [{ id, name, variables:[{ key }] }]           (names only are read)
 *   boards        [{ id, name, scope, location }]
 *   tasks         [{ id, title, scope_id (board id), done, status, scope, location }]
 *   docNodes      [{ id, type:'folder'|'document', name, parentId, scope, location, scopeId? }]
 *   docWorkspaces [{ id, name }]                                 (personal docscope rows)
 *   docBodies     [{ docId, bodyHtml }]
 *   appointments  [{ uid, title, start, start_date, scope, location }]
 *   dbConnections [{ id, name, engine }]
 *   sshHosts      [{ id, name }]
 *   repos         [{ id, name, provider, host, remote, scope, location }]  (never a local path)
 *   projects      [{ id, name, color, description, scope, location }]
 *   links         [{ id, from, to, rel, scope }]                 (explicit, node ids)
 *
 * `scope` is the WIRE scope (`personal` | `team:<id>`) and `location` is
 * cloud | device | team (graphScopes.js). Rows without them (older callers,
 * collections, vault items) produce nodes without them. When one id arrives
 * twice, the FIRST row wins — the snapshot orders scopes personal → device →
 * teams by id, so that is the collision rule.
 */
export function buildGraph(s = {}) {
  const nodes = new Map();
  const edges = new Map();

  const addNode = (type, id, label, extra) => {
    if (id == null || id === '') return null;
    const key = nodeId(type, id);
    if (!nodes.has(key)) nodes.set(key, { id: key, type, label, ...(extra || {}) });
    return key;
  };
  const addEdge = (from, to, rel, origin, anchor, linkId, scope) => {
    if (!from || !to || from === to) return;
    const key = `${from}|${rel}|${to}|${origin}`;
    if (edges.has(key)) return;
    const edge = { id: linkId || key, from, to, rel, origin };
    if (anchor) edge.anchor = anchor;
    if (scope) edge.scope = scope; // where an explicit link's record lives
    edges.set(key, edge);
  };

  // ── collections, folders, requests ───────────────────────────────────────
  const requests = [];
  for (const c of s.collections || []) {
    const col = addNode('collection', c?.id, str(c?.name, 'Untitled collection'));
    if (!col) continue;
    const folders = Array.isArray(c.settings?.folders) ? c.settings.folders : [];
    const folderIds = new Set();
    for (const f of folders) {
      if (addNode('folder', f?.id, str(f?.name, 'Folder'))) folderIds.add(String(f.id));
    }
    for (const f of folders) {
      if (f?.id == null) continue;
      const parent = f.parent_id != null && folderIds.has(String(f.parent_id)) ? nodeId('folder', f.parent_id) : col;
      addEdge(parent, nodeId('folder', f.id), 'contains', 'derived');
    }
    for (const r of c.requests || []) {
      // Never fall back to the URL: it can carry a token in its query string
      // (review finding 1). A nameless request is labelled by its method.
      const method = r?.method ? String(r.method).toUpperCase() : '';
      const req = addNode('request', r?.id, str(r?.name, method ? `${method} request` : 'Request'), {
        method: method || undefined,
      });
      if (!req) continue;
      const parent = r.folder_id != null && folderIds.has(String(r.folder_id)) ? nodeId('folder', r.folder_id) : col;
      addEdge(parent, req, 'contains', 'derived');
      requests.push({ key: req, request: r });
    }
  }

  // ── environments + {{variable}} uses ─────────────────────────────────────
  const envsByVar = new Map();
  for (const e of s.environments || []) {
    const env = addNode('environment', e?.id, str(e?.name, 'Environment'));
    if (!env) continue;
    for (const v of e.variables || []) {
      const name = str(v?.key, '');
      if (!name) continue;
      if (!envsByVar.has(name)) envsByVar.set(name, new Set());
      envsByVar.get(name).add(env);
    }
  }
  for (const { key, request } of requests) {
    for (const [name, field] of requestVariableUses(request)) {
      for (const env of envsByVar.get(name) || []) {
        addEdge(key, env, 'uses', 'derived', { field, variable: name });
      }
    }
  }

  // ── boards + tasks ───────────────────────────────────────────────────────
  for (const b of s.boards || []) addNode('board', b?.id, str(b?.name, 'Untitled board'), whereOf(b));
  for (const t of s.tasks || []) {
    const task = addNode('task', t?.id, str(t?.title, 'Untitled task'), {
      done: t?.done === true,
      // To do · In progress · Done · Dismissed — `done` alone shows Dismissed as done (MCP audit, card ef074711).
      status: taskStatusOf(t),
      ...whereOf(t),
    });
    const board = t?.scope_id != null ? nodeId('board', t.scope_id) : null;
    if (task && board && nodes.has(board)) addEdge(board, task, 'contains', 'derived');
  }

  // ── documents ────────────────────────────────────────────────────────────
  for (const d of s.docNodes || []) {
    if (d?.type === 'folder') addNode('doc_folder', d.id, str(d.name, 'Folder'), whereOf(d));
    else if (d?.type === 'document') addNode('document', d.id, str(d.name ?? d.title, 'Untitled'), whereOf(d));
  }
  for (const d of s.docNodes || []) {
    if (d?.id == null || d.parentId == null) continue;
    const child = nodeId(d.type === 'folder' ? 'doc_folder' : 'document', d.id);
    const parent = nodes.has(nodeId('doc_folder', d.parentId))
      ? nodeId('doc_folder', d.parentId)
      : nodeId('document', d.parentId); // subpages
    if (nodes.has(child) && nodes.has(parent)) addEdge(parent, child, 'contains', 'derived');
  }
  // A workspace groups its roots (personal documents only — graphDocWorkspaces.js).
  addDocWorkspaces(s, { nodes, nodeId, addNode, addEdge });
  for (const b of s.docBodies || []) {
    const from = nodeId('document', b?.docId);
    if (!nodes.has(from)) continue;
    for (const target of documentLinks(b.bodyHtml)) {
      const to = nodeId('document', target);
      if (nodes.has(to)) addEdge(from, to, 'mentions', 'derived', { field: 'body' });
    }
  }

  // ── calendar, vault-backed connections ───────────────────────────────────
  for (const a of s.appointments || []) {
    addNode('appointment', a?.uid ?? a?.id, str(a?.title, 'Appointment'), {
      at: a?.start || a?.start_date || undefined,
      ...whereOf(a),
    });
  }
  for (const c of s.dbConnections || []) {
    addNode('db_connection', c?.id, str(c?.name, 'Database'), { engine: c?.engine || undefined });
  }
  for (const h of s.sshHosts || []) addNode('ssh_host', h?.id, str(h?.name, 'Host'));

  // ── code repositories: the remote (stored without credentials), never a local path ──
  for (const r of s.repos || []) {
    addNode('git_repo', r?.id, str(r?.name, 'Repository'), {
      provider: r?.provider || undefined,
      host: r?.host || undefined,
      remote: r?.remote ? withoutUser(r.remote) : undefined, // no user, query or fragment
      ...whereOf(r),
    });
  }

  // ── projects + explicit links (last: they point at what exists) ──────────
  for (const p of s.projects || []) {
    const solid = p?.color ? projectSolidColor(p.color) : undefined;
    addNode('project', p?.id, str(p?.name, 'Untitled project'), { color: solid, ...whereOf(p) });
  }
  for (const l of s.links || []) {
    // A link to something deleted (or not loaded, e.g. vault locked) is kept in
    // storage but not drawn — the graph never invents a node.
    if (!nodes.has(l?.from) || !nodes.has(l?.to)) continue;
    const rel = RELATIONS.includes(l.rel) ? l.rel : 'relates_to';
    addEdge(l.from, l.to, rel, 'explicit', null, l.id, l.scope);
  }

  return { version: GRAPH_VERSION, nodes: [...nodes.values()], edges: [...edges.values()] };
}
